// ---------------------------------------------------------------------------
// humanizeRuleId — rule-id → creator-language translation layer (F2).
//
// The live rule-id population reaching the studio aggregator today:
//   1. Generated SHACL shape ids (`@cogo/kg-repository` compile-shacl output,
//      consumed via `features/validation/shacl-rules.ts`):
//        `cogo:<Class>Shape#<property>-<constraint>` where constraint is one
//        of min-count | max-count | datatype | in | pattern (332 rules).
//   2. Studio synthetic rules from `run-validation.ts`:
//        `extract-block-jsonld:<blockId>` and `validate-error:<blockId>`.
//
// Anything else falls through to a prettifier that strips IRI/prefix noise
// and renders sentence case — it NEVER echoes a raw IRI back at a creator.
// Ontology vocabulary stays internal per docs/ide-library-ia.md.
// ---------------------------------------------------------------------------

const SHACL_SHAPE_RE =
  /^(?:[\w.-]+:)?(\w+?)Shape#(.+?)-(min-count|max-count|datatype|in|pattern)$/

const CONSTRAINT_PHRASES: Record<string, (cls: string, prop: string) => string> = {
  'min-count': (cls, prop) => `${cls} is missing "${prop}"`,
  'max-count': (cls, prop) => `${cls} has more than one "${prop}" value`,
  datatype: (cls, prop) => `${cls} "${prop}" has the wrong value type`,
  in: (cls, prop) => `${cls} "${prop}" is not one of the allowed values`,
  pattern: (cls, prop) => `${cls} "${prop}" has an invalid format`,
}

/** "DesignSystem" → "Design system"; "Action" → "Action". */
function splitCamel(name: string): string {
  const spaced = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

/** Last meaningful fragment of an id/IRI, kebab/snake/camel → sentence case. */
function prettify(ruleId: string): string {
  let fragment = ruleId
  for (const sep of ['#', '/', ':']) {
    const idx = fragment.lastIndexOf(sep)
    if (idx >= 0 && idx < fragment.length - 1) fragment = fragment.slice(idx + 1)
  }
  const words = fragment
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .toLowerCase()
  if (!words) return 'Validation rule'
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * Translate a validator rule id into a creator-facing title.
 * Total — every input yields plain language, unknown ids are prettified.
 */
export function humanizeRuleId(ruleId: string): string {
  const extract = ruleId.match(/^extract-block-jsonld:(.+)$/)
  if (extract) return `Could not read block "${extract[1]}" for validation`

  const validateError = ruleId.match(/^validate-error:(.+)$/)
  if (validateError) return `Validation could not run on block "${validateError[1]}"`

  const shacl = ruleId.match(SHACL_SHAPE_RE)
  if (shacl) {
    const [, cls, prop, constraint] = shacl
    return CONSTRAINT_PHRASES[constraint](splitCamel(cls), prop)
  }

  return prettify(ruleId)
}
